import cloneDeep from 'lodash/cloneDeep';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import { computed, ref } from 'vue';

import { getRuleFilterMatchCount } from '@/apis/community';
import { COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET } from '@/app/community/constants';
import {
  CHANNEL_FILTER_OPTIONS,
  MESSAGE_TYPE_FILTER_OPTIONS,
} from '@/app/community/constants/communityFilters';
import {
  getFilterItemLabel,
  LIKE_COMMENTS_VALIDATION_REASONS,
  MESSAGE_RULE_FILTER_OPTIONS,
  MESSAGE_RULE_OPERATOR_TYPES,
  MESSAGE_RULE_TYPES,
  SEND_DM_FROM_COMMENT_VALIDATION_REASONS,
} from '@/app/community/constants/inboxAutomation';
import { extractQuotedKeyword } from '@/app/community/utils/keyword-filters';

export function useCommunityMessageRuleBuilder() {
  const INITIAL_RULE_ACTIONS = [];
  const GROUP_STRUCTURE = {
    items: [], // the filters inside a group will be added here
    operator: 'OR',
  };
  const INITIAL_RULE_CONDITION_GROUPS = [
    {
      previousGroupJoiningOperator: null,
      groupDetails: cloneDeep(GROUP_STRUCTURE),
    },
  ];

  const conditionGroups = ref(cloneDeep(INITIAL_RULE_CONDITION_GROUPS));
  const actions = ref(cloneDeep(INITIAL_RULE_ACTIONS));
  const filterMatchedMessagesCount = ref(null);

  const isFiltersComplete = computed(() => {
    let isComplete = true;
    conditionGroups.value.forEach((group) => {
      const items = group.groupDetails.items;
      if (!items.every((item) => !isNil(item.key) && !isNil(item.value) && item.value !== '')) {
        isComplete = false;
      }
    });
    return isComplete;
  });

  const emptyGroupCount = computed(() =>
    conditionGroups.value.reduce(
      (count, group) => (group.groupDetails.items.length === 0 ? count + 1 : count),
      0,
    ),
  );

  const isActionsComplete = computed(() => {
    const action = actions.value?.[0];
    return !!action && !!action.key && !!action.value;
  });
  const hasRepliedFilter = computed(() => {
    let exist = false;
    conditionGroups.value.forEach((group) => {
      const items = group.groupDetails.items;
      if (
        items.length &&
        items.some((item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.REPLIED.key)
      ) {
        exist = true;
      }
    });
    return exist;
  });

  const allChannels = Object.values(CHANNEL_FILTER_OPTIONS);

  // Any condition row selecting multiple channels makes the rule invalid for single-channel actions.
  const hasMultiChannelSelection = computed(() =>
    conditionGroups.value.some((group) => {
      const items = group.groupDetails.items;
      const channelValues = items
        .filter((item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key)
        .map((item) => {
          const value = item.value;
          return value && typeof value === 'object' ? value.value : value;
        })
        .filter((value) => value !== undefined && value !== null);
      return uniq(channelValues).length > 1;
    }),
  );

  // Get the channel value from an item, normalizing object format
  function getChannelValue(item) {
    if (item.key !== MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key) return null;
    const value = item.value;
    return value && typeof value === 'object' ? value.value : value;
  }

  // Get all channel values from a group's items
  function getGroupChannels(items) {
    return items.map(getChannelValue).filter(Boolean);
  }

  // Check if any channel filter is joined with 'OR' to non-channel filters, making the channel optional.
  const isChannelFilterOrCondition = computed(() => {
    const groups = conditionGroups.value;

    // Check within each group: if items are ORed and contain both channel and non-channel filters
    const hasOrWithinGroup = groups.some((group) => {
      const items = group.groupDetails.items;
      const operator = group.groupDetails.operator || MESSAGE_RULE_OPERATOR_TYPES.OR;
      if (operator === MESSAGE_RULE_OPERATOR_TYPES.OR && items.length > 1) {
        const hasChannel = items.some(
          (item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key,
        );
        const hasNonChannel = items.some(
          (item) => item.key !== MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key,
        );
        return hasChannel && hasNonChannel;
      }
      return false;
    });

    if (hasOrWithinGroup) return true;

    // Build AND-blocks: consecutive groups connected by AND form a single logical block
    // Groups connected by OR separate blocks
    const andBlocks = groups.reduce((blocks, group, i) => {
      if (i === 0 || group.previousGroupJoiningOperator === MESSAGE_RULE_OPERATOR_TYPES.AND) {
        const lastBlock = blocks[blocks.length - 1] || [];
        lastBlock.push(group);
        if (blocks.length === 0) blocks.push(lastBlock);
      } else {
        blocks.push([group]);
      }
      return blocks;
    }, []);

    // If only one block, no OR between groups
    if (andBlocks.length <= 1) return false;

    // For each AND-block, collect all channels from all groups in the block
    const blockChannels = andBlocks.map((block) => {
      const channels = block.flatMap((g) => getGroupChannels(g.groupDetails.items));
      return uniq(channels);
    });

    // Check if all OR-connected blocks have the same single channel
    const blocksWithChannels = blockChannels.filter((channels) => channels.length > 0);
    const blocksWithoutChannels = blockChannels.filter((channels) => channels.length === 0);

    // If any block has no channel but others have channels, channel is optional
    if (blocksWithoutChannels.length > 0 && blocksWithChannels.length > 0) {
      return true;
    }

    // If blocks have different channels, channel is optional (different channels in OR)
    if (blocksWithChannels.length > 1) {
      const allBlockChannels = blocksWithChannels.flat();
      if (uniq(allBlockChannels).length > 1) {
        return true;
      }
    }

    return false;
  });

  // Message type filters can restrict platforms (e.g. STORY_MENTION is only for INSTAGRAM).
  // We normalize here because the value can be a raw string (from backend) or an option object (from UI).
  function getMessageTypePlatforms(value) {
    const normalized = value && typeof value === 'object' ? value.value : value;
    if (!normalized) return allChannels;
    const option =
      MESSAGE_TYPE_FILTER_OPTIONS[normalized] ||
      Object.values(MESSAGE_TYPE_FILTER_OPTIONS).find((opt) => opt.value === normalized);
    return option?.platforms || allChannels;
  }

  function getChannelSetForFilterItem(item) {
    if (!item?.key) return allChannels;
    if (item.key === MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key) {
      const value = item.value;
      const channel = value && typeof value === 'object' ? value.value : value;
      return channel ? [channel] : allChannels;
    }
    if (item.key === MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key) {
      return getMessageTypePlatforms(item?.value);
    }
    return allChannels;
  }

  // Evaluate the filter tree into the set of possible channels (OR = union, AND = intersection).
  function deriveChannelSet(node) {
    if (!node) return allChannels;
    if (!Array.isArray(node.items)) {
      return getChannelSetForFilterItem(node);
    }
    const operator = node.operator || MESSAGE_RULE_OPERATOR_TYPES.OR;
    let result = null;
    node.items.forEach((item) => {
      const next = deriveChannelSet(item);
      if (result === null) {
        result = next;
        return;
      }
      result =
        operator === MESSAGE_RULE_OPERATOR_TYPES.AND
          ? intersection(result, next)
          : union(result, next);
    });
    return result ?? allChannels;
  }

  // Convert nested filter tree into explicit OR branches.
  // Each returned branch is one concrete match path with its effective channel set.
  function expandFilterBranches(node) {
    if (!node) {
      return [{ items: [], channelSet: allChannels }];
    }

    if (!Array.isArray(node.items)) {
      return [{ items: [node], channelSet: getChannelSetForFilterItem(node) }];
    }

    const operator = node.operator || MESSAGE_RULE_OPERATOR_TYPES.OR;
    const branchSets = node.items.map((item) => expandFilterBranches(item));

    if (operator === MESSAGE_RULE_OPERATOR_TYPES.AND) {
      return branchSets.reduce(
        (acc, branches) =>
          acc.flatMap((currentBranch) =>
            branches.map((nextBranch) => ({
              items: [...currentBranch.items, ...nextBranch.items],
              channelSet: intersection(currentBranch.channelSet, nextBranch.channelSet),
            })),
          ),
        [{ items: [], channelSet: allChannels }],
      );
    }

    return branchSets.flat();
  }

  function getFilterUsed() {
    const filterLabels = conditionGroups.value.flatMap((g) =>
      g.groupDetails.items.map((item) => getFilterItemLabel(item)),
    );
    return uniq(filterLabels);
  }

  function getFilterGroups() {
    const filterGroups = conditionGroups.value.map((g) => {
      const items = g.groupDetails.items;
      return {
        filter: items.map((item) => getFilterItemLabel(item)),
        value: items.map((item) => item.value),
        operator: g.previousGroupJoiningOperator,
      };
    });
    return filterGroups;
  }

  function clearFilterMessageCountAndError() {
    filterMatchedMessagesCount.value = null;
  }

  function clearRuleBuilder() {
    conditionGroups.value = cloneDeep(INITIAL_RULE_CONDITION_GROUPS);
    actions.value = cloneDeep(INITIAL_RULE_ACTIONS);
    clearFilterMessageCountAndError();
  }

  function updateGroup(groupIndex, groupItems, ruleType) {
    if (ruleType === MESSAGE_RULE_TYPES.ACTION) {
      actions.value = groupItems;
    } else {
      conditionGroups.value[groupIndex].groupDetails.items = groupItems;
    }
  }

  function removeFilterGroup(groupIndex) {
    if (conditionGroups.value[groupIndex]) {
      conditionGroups.value.splice(groupIndex, 1);
    }
  }

  function updateGroupOperator(groupIndex, joiningOperator) {
    if (conditionGroups.value[groupIndex]) {
      conditionGroups.value[groupIndex].previousGroupJoiningOperator = joiningOperator;
    }
  }

  function addGroupAfter(groupIndex, joiningOperator) {
    const newGroup = {
      previousGroupJoiningOperator: joiningOperator,
      groupDetails: cloneDeep(GROUP_STRUCTURE),
    };
    conditionGroups.value.splice(groupIndex + 1, 0, newGroup);
  }

  function buildJSONRuleFilters() {
    if (conditionGroups.value.length === 0) return null;

    const nonEmptyGroups = conditionGroups.value.filter(
      (group) => group.groupDetails.items.length > 0,
    );

    let finalFilters = {
      items: [],
      operator: 'OR',
    };

    let currentAndGroup = null;
    let hasOrOperator = false;

    nonEmptyGroups.forEach((group, index) => {
      const processedItems = group.groupDetails.items.map((item) => {
        if (item.key === MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT.key) {
          if (item.value === COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.SPAM.messageContentValue) {
            return { key: COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.SPAM.key, value: true };
          }
          if (item.value === COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.QUESTION.messageContentValue) {
            return { key: COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.QUESTION.key, value: true };
          }
        }
        return item;
      });

      const processedGroup = {
        ...group.groupDetails,
        items: processedItems,
      };

      if (group.previousGroupJoiningOperator === 'AND') {
        if (!currentAndGroup) {
          // Create a new AND group and include the previous group inside it
          currentAndGroup = {
            items: [],
            operator: 'AND',
          };
          if (index > 0) {
            // Add the previous group to the new AND group
            currentAndGroup.items.push(finalFilters.items.pop());
          }
        }
        currentAndGroup.items.push(processedGroup);
      } else {
        // Check for OR operator explicitly, ignore the first group with null
        if (group.previousGroupJoiningOperator === 'OR') {
          hasOrOperator = true;
        }
        if (currentAndGroup) {
          finalFilters.items.push(currentAndGroup);
          currentAndGroup = null;
        }
        finalFilters.items.push(processedGroup);
      }
    });

    if (currentAndGroup) {
      finalFilters.items.push(currentAndGroup);
    }

    // If there were no OR operators and all conditionGroups are combined with AND, change the outermost operator to AND
    if (!hasOrOperator) {
      finalFilters = finalFilters.items[0];
    }

    return finalFilters || {};
  }

  function buildJSONRuleActions() {
    const finalActions = {};
    if (isActionsComplete.value) {
      actions.value.forEach((action) => {
        finalActions[action.key] = action.value;
      });
    }
    return finalActions;
  }

  function buildJSONRule() {
    const finalFilters = buildJSONRuleFilters();
    const finalActions = buildJSONRuleActions();
    return { actions: finalActions, filters: finalFilters };
  }

  function checkGroupHasCompleteItem(index) {
    const items = conditionGroups.value[index]?.groupDetails.items;
    return items?.length > 0 && items.some((item) => !isNil(item.key) && !isNil(item.value));
  }

  async function updateFilterMatchCount(selectedBrandId) {
    if (emptyGroupCount.value === conditionGroups.value.length) {
      clearFilterMessageCountAndError();
      return;
    }
    if (isFiltersComplete.value) {
      try {
        const JSONFilters = buildJSONRuleFilters();
        const res = await getRuleFilterMatchCount(selectedBrandId, JSONFilters);
        filterMatchedMessagesCount.value = res.data.count;
      } catch {
        // Match-count fetch failed; leave the previous count in place.
      }
    }
  }

  const ruleDetail = computed(() => buildJSONRule());

  const ruleChannelSet = computed(() => deriveChannelSet(ruleDetail.value?.filters));
  const isSingleChannelRule = computed(
    () => !hasMultiChannelSelection.value && ruleChannelSet.value.length === 1,
  );

  const sendDmFromCommentValidation = computed(() => {
    const allowedChannels = [CHANNEL_FILTER_OPTIONS.INSTAGRAM, CHANNEL_FILTER_OPTIONS.FACEBOOK];
    const allowedMessageTypes = [
      MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
      MESSAGE_TYPE_FILTER_OPTIONS.ADS_COMMENT.value,
    ];
    const reasons = new Set();
    const isAllowedMessageType = (value) => {
      const normalizedValue = value && typeof value === 'object' ? value.value : value;
      return allowedMessageTypes.includes(normalizedValue);
    };
    const addKeywordReasons = (items) => {
      const keywordItems = items.filter(
        (item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.KEYWORD.key,
      );
      if (!keywordItems.length) {
        reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.KEYWORD);
        return;
      }

      const hasInvalidKeyword = keywordItems.some(
        (item) => typeof item.value !== 'string' || extractQuotedKeyword(item.value) === null,
      );
      if (hasInvalidKeyword) {
        reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.KEYWORD_EXACT_MATCH);
      }
    };

    const explicitChannels = uniq(
      conditionGroups.value.flatMap((group) => getGroupChannels(group.groupDetails.items)),
    );
    const selectedChannel = explicitChannels.length === 1 ? explicitChannels[0] : null;
    if (!selectedChannel) {
      reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.CHANNEL_IN_EACH_BRANCH);
    } else if (!allowedChannels.includes(selectedChannel)) {
      reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.UNSUPPORTED_CHANNEL);
    }

    const branches = expandFilterBranches(ruleDetail.value?.filters);

    // Check all branches, collecting all unique failure reasons.
    branches.forEach((branch) => {
      if (selectedChannel) {
        // Every branch must have the same channel.
        const branchChannels = uniq(getGroupChannels(branch.items));
        if (branchChannels.length !== 1 || branchChannels[0] !== selectedChannel) {
          reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.CHANNEL_IN_EACH_BRANCH);
        }
      }

      const messageTypeItems = branch.items.filter(
        (item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key,
      );
      if (messageTypeItems.length !== 1 || !isAllowedMessageType(messageTypeItems[0].value)) {
        reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.MESSAGE_TYPE);
      }

      addKeywordReasons(branch.items);
    });

    // When no branches could be formed, scan condition groups directly for message type and keyword.
    if (!branches.length) {
      const allItems = conditionGroups.value.flatMap((g) => g.groupDetails.items);
      const hasValidMessageType = allItems.some(
        (item) =>
          item.key === MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key &&
          isAllowedMessageType(item.value),
      );
      if (!hasValidMessageType) {
        reasons.add(SEND_DM_FROM_COMMENT_VALIDATION_REASONS.MESSAGE_TYPE);
      }

      addKeywordReasons(allItems);
    }

    const reasonsArray = [...reasons];
    return reasonsArray.length
      ? { valid: false, reasons: reasonsArray }
      : { valid: true, reasons: [] };
  });

  const likeCommentsValidation = computed(() => {
    // The Like action carries no platform-specific payload, so any mix of Instagram,
    // Facebook, and TikTok (OR-ed) is fine — as long as every branch is a comment
    // (never a DM or mention) and never targets an unsupported channel.
    const allowedChannels = [
      CHANNEL_FILTER_OPTIONS.INSTAGRAM,
      CHANNEL_FILTER_OPTIONS.FACEBOOK,
      CHANNEL_FILTER_OPTIONS.TIKTOK,
    ];
    const allowedMessageTypes = [
      MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
      MESSAGE_TYPE_FILTER_OPTIONS.ADS_COMMENT.value,
    ];
    const reasons = new Set();
    const isAllowedMessageType = (value) => {
      const normalizedValue = value && typeof value === 'object' ? value.value : value;
      return allowedMessageTypes.includes(normalizedValue);
    };

    // An empty OR filter expands to no branches; treat it as a single empty branch so a
    // rule with no usable filters fails both requirements.
    const branches = expandFilterBranches(ruleDetail.value?.filters);
    const effectiveBranches = branches.length ? branches : [{ items: [], channelSet: allChannels }];

    effectiveBranches.forEach((branch) => {
      // Every branch must be limited to supported channels — a branch that could also
      // match an unsupported channel (e.g. YouTube, X), or has no channel constraint at
      // all, is invalid. An empty channel set means contradictory channel constraints
      // (e.g. "IG or TikTok" AND "FB or YouTube") that resolve to no channel at all, so
      // it can't be confined to supported channels and is treated as invalid too.
      if (
        !branch.channelSet.length ||
        branch.channelSet.some((channel) => !allowedChannels.includes(channel))
      ) {
        reasons.add(LIKE_COMMENTS_VALIDATION_REASONS.UNSUPPORTED_CHANNEL);
      }

      // Like only acts on comments, so every branch must filter to a comment message
      // type (Comments or Ad Comments) — never a DM, mention, or other message type.
      const messageTypeItems = branch.items.filter(
        (item) => item.key === MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key,
      );
      if (messageTypeItems.length !== 1 || !isAllowedMessageType(messageTypeItems[0].value)) {
        reasons.add(LIKE_COMMENTS_VALIDATION_REASONS.MESSAGE_TYPE);
      }

      // Beyond channel and message type, every branch must narrow the rule with at least
      // one additional filter so automated likes stay under Meta's burst rate limit.
      // Require a real filter key so an empty placeholder item (from a rule with no
      // filters) doesn't count as an additional filter.
      const hasAdditionalFilter = branch.items.some(
        (item) =>
          item.key &&
          item.key !== MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key &&
          item.key !== MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key,
      );
      if (!hasAdditionalFilter) {
        reasons.add(LIKE_COMMENTS_VALIDATION_REASONS.ADDITIONAL_FILTER);
      }
    });

    const reasonsArray = [...reasons];
    return reasonsArray.length
      ? { valid: false, reasons: reasonsArray }
      : { valid: true, reasons: [] };
  });

  function processRuleFilterGroupItem(filter, groupOperator = null) {
    if (filter.items[0].operator) {
      filter.items.forEach((nestedFilter, index) => {
        const operator = index === 0 ? groupOperator : filter.operator;
        processRuleFilterGroupItem(nestedFilter, operator);
      });
    } else {
      const convertedItems = filter.items.map((item) => {
        const widgetItem = Object.values(COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET).find(
          (widget) => widget.key === item.key,
        );
        if (widgetItem && item.value === true) {
          return {
            key: MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT.key,
            value: widgetItem.messageContentValue,
          };
        }
        return { ...item };
      });

      conditionGroups.value.push({
        groupDetails: { ...filter, items: convertedItems },
        previousGroupJoiningOperator: conditionGroups.value.length ? groupOperator : null,
      });
    }
  }

  function setRuleFiltersFromJSON(rawFilters) {
    conditionGroups.value = [];
    processRuleFilterGroupItem(rawFilters);
  }

  function setRuleActionsFromJSON(rawActions) {
    actions.value = Object.entries(rawActions)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => {
        if (a.key === 'archive') return 1;
        if (b.key === 'archive') return -1;
        return 0;
      });
  }

  function isRuleDetailChanged(original) {
    const current = buildJSONRule();
    return !isEqual(original, current);
  }

  return {
    conditionGroups,
    actions,
    isFiltersComplete,
    isActionsComplete,
    hasRepliedFilter,
    filterMatchedMessagesCount,
    ruleDetail,
    ruleChannelSet,
    isSingleChannelRule,
    hasMultiChannelSelection,
    isChannelFilterOrCondition,
    sendDmFromCommentValidation,
    likeCommentsValidation,
    updateGroup,
    removeFilterGroup,
    updateGroupOperator,
    addGroupAfter,
    buildJSONRule,
    buildJSONRuleFilters,
    buildJSONRuleActions,
    clearRuleBuilder,
    checkGroupHasCompleteItem,
    updateFilterMatchCount,
    setRuleActionsFromJSON,
    setRuleFiltersFromJSON,
    isRuleDetailChanged,
    getFilterUsed,
    getFilterGroups,
    clearFilterMessageCountAndError,
  };
}
